// Shared between the recipe-creation Action List builder (ActionList.js) and
// the Forms library's reusable field-template editor
// (Configurations/ActionListField.js) — both need the exact same type list
// so a template built in the library maps onto the same "Field Type" options
// an operator sees when configuring an operation.
export const ACTION_FIELD_TYPES = [
  { value: 'text', label: 'Text Input', dataType: 'string' },
  { value: 'number', label: 'Number Input', dataType: 'number' },
  { value: 'dropdown', label: 'Dropdown', dataType: 'string' },
  { value: 'radio', label: 'Radio Button', dataType: 'string' },
  { value: 'date', label: 'Date Picker', dataType: 'string' },
  { value: 'time', label: 'Time Picker', dataType: 'string' },
  { value: 'checkbox', label: 'Checkbox', dataType: 'boolean' },
  { value: 'button', label: 'Button', dataType: 'string' },
  { value: 'notification', label: 'Notification', dataType: 'string' },
  // A step that is itself another configured action (from the same actions
  // library the top-level Action select uses), holding its own nested steps.
  // Available at any depth in the step tree, including the top-level fields
  // array — recurses unlimited depth: action -> steps -> action -> steps -> …
  { value: 'action', label: 'Nested Action', dataType: 'string' },
];

// Every condition source a Notification field can watch, once it's actually
// configured inside a real operation (ActionList.js).
export const NOTIFICATION_CONDITION_SOURCES = [
  { value: 'none', label: 'Notification Only' },
  { value: 'duration', label: 'Duration' },
  { value: 'ingredient', label: 'Ingredient Quantity' },
  { value: 'monitoring', label: 'Monitoring Parameter' },
  { value: 'sampling', label: 'Sampling Result' },
  { value: 'prior_action', label: 'Prior Action Response' },
];

// Subset safe to offer in the Forms library's reusable template — the other
// three (Ingredient/Monitoring/Prior Action) each need to reference a real
// per-operation entity (a specific ingredient, monitoring parameter, or an
// earlier action step in the same operation) that doesn't exist yet at
// library-authoring time. "Sampling Result" only needs a generic Pass/Fail
// expectation (see ActionList.js's `src === 'sampling'` block — no id
// reference at all), so it's genuinely reusable, unlike the other three.
export const LIBRARY_NOTIFICATION_SOURCES = NOTIFICATION_CONDITION_SOURCES.filter((s) =>
  ['none', 'duration', 'sampling'].includes(s.value)
);

export const NOTIFICATION_TIMING = [
  { value: 'at_start', label: 'At Operation Start' },
  { value: 'after_duration', label: 'After Duration' },
  { value: 'at_end', label: 'At Operation End' },
];

export const NOTIFY_SEVERITY = [
  { value: 'info', label: 'Info' },
  { value: 'notification', label: 'Notification' },
  { value: 'alert', label: 'Alert' },
];

export const DURATION_UNITS = [
  { value: 'sec', label: 'Seconds' },
  { value: 'min', label: 'Minutes' },
  { value: 'hrs', label: 'Hours' },
];

export const DURATION_BASIS = [
  { value: 'absolute', label: 'Absolute Time' },
  { value: 'percent', label: '% Elapsed' },
];

export const DURATION_OPERATORS = [
  { value: 'exceeded', label: 'Exceeded' },
  { value: 'not_started', label: 'Not Started Within' },
  { value: 'pending', label: 'Still Pending After' },
];

// Matches ActionList.js's real Formik keys 1:1 (see its NOTIF_BASE /
// NOTIF_SOURCE_FIELDS) so a template built here can be spread straight onto
// that shape at apply-time without any key translation. Only the fields
// actually editable in the library are listed — everything else
// (target, notify_role_ids, channels, message_is_html, response_fields …)
// keeps ActionList.js's own defaults when the template is applied.
export const emptyLibraryNotification = {
  condition_source: 'none',
  timing: 'at_start',
  timing_duration_value: null,
  timing_duration_unit: 'min',
  duration_basis: 'absolute',
  duration_operator: 'exceeded',
  duration_value: null,
  duration_unit: 'min',
  sampling_expected: 'pass',
  severity: 'notification',
  message: '',
  message_is_html: false,
};
